import React, { useEffect, useState } from 'react';
import {
  ActionGroup,
  Button,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  FormGroupLabelHelp,
  Popover,
  TextInput,
} from '@patternfly/react-core';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../../api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSetUserContext, useUserContext } from '../../contexts/user/UserContext';
import { isEqual } from 'lodash';
import { ApiResponseUser } from '../../types/api/ApiResponseUser';
import { User } from '../../types/models';

// Interface for form data structure
interface UserFormData {
  firstname: string;
  lastname: string;
  email: string;
}

function UserProfileForm() {
  const user = useUserContext();
  const setUser = useSetUserContext();
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const { addNotification, setDrawerOpen } = useNotifications();

  // Store the original values from user context
  const [originalValues, setOriginalValues] = useState<UserFormData>({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    email: user?.email || '',
  });

  const [formData, setFormData] = useState<UserFormData>({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    email: user?.email || '',
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const userIDHelpRef = React.useRef(null);
  const clientIDHelpRef = React.useRef(null);
  const subHelpRef = React.useRef(null);
  const emailHelpRef = React.useRef(null);

  const [isChanged, setIsChanged] = useState<boolean>(false);

  // Update original values if user context changes
  useEffect(() => {
    const newOriginalValues = {
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      email: user?.email || '',
    };
    setOriginalValues(newOriginalValues);
    setFormData(newOriginalValues);
  }, [user]);

  // Check for changes whenever formData is updated
  useEffect(() => {
    const hasChanged = !isEqual(formData, originalValues);
    setIsChanged(hasChanged);
  }, [formData, originalValues]);

  const handleAddSuccess = () => {
    addNotification({
      title: 'Profile update Successful',
      srTitle: 'Success alert',
      description: 'Operation completed successfully',
      variant: 'success',
    });
    setDrawerOpen(true);
  };

  const handleAddDanger = () => {
    addNotification({
      title: 'Profiled update Error',
      srTitle: 'Error alert',
      description: 'An error occurred during the operation, please review the logs.',
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const handleInputChange = (event, value: string) => {
    const { name } = event.currentTarget;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Let's start by setting the correct states
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    if (user !== null && auth.isAuthenticated) {
      try {
        const response = await apiFetch(`/users/${user.id}`, {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            firstname: formData.firstname,
            lastname: formData.lastname,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update profile');
        }

        const result: ApiResponseUser = await response.json();

        if (result.status === 'success') {
          setOriginalValues(formData);
          // Update only if we have a current user
          if (user) {
            // Create a new user object maintaining all existing properties
            const updatedUser: User = {
              ...user,
              firstname: result.data.user.firstname,
              lastname: result.data.user.lastname,
            };
            setUser(updatedUser);
          }
          setIsChanged(false);
          setSuccessMessage('Profile updated successfully');

          // If you need to update the user context with the returned data
          // updateUser(result.data.user);
          handleAddSuccess();
        } else {
          handleAddDanger();
          throw new Error('Failed to update profile');
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      email: user?.email || '',
    });
    setIsChanged(false);
  };

  return (
    <Card isPlain={true}>
      <CardTitle>Immutable Properties</CardTitle>
      <CardBody>
        <Form>
          <FormGroup
            label="User ID"
            fieldId="userId"
            labelHelp={
              <Popover
                triggerRef={userIDHelpRef}
                headerContent={<div>This is your user ID, it is immutable.</div>}
                bodyContent={
                  <div>
                    Your userID is linked to your Keycloak roles, which control what you can do in the application (like
                    editing DNS zones or creating virtual machines,...).
                  </div>
                }
              >
                <FormGroupLabelHelp ref={userIDHelpRef} aria-label="More info for name field" />
              </Popover>
            }
          >
            <TextInput id="userId" value={user?.id || ''} isDisabled />
          </FormGroup>

          <FormGroup
            label="Client ID"
            fieldId="clientId"
            labelHelp={
              <Popover
                triggerRef={clientIDHelpRef}
                headerContent={<div>This is your client ID, it is immutable.</div>}
                bodyContent={
                  <div>
                    Your clientID connects you to your organization&apos;s account, determining which resources and
                    services are available to your team. <br />
                    If this field is empty, it means you must first finalize your sign-up to enter invoicing information
                  </div>
                }
              >
                <FormGroupLabelHelp ref={clientIDHelpRef} aria-label="More info for name field" />
              </Popover>
            }
          >
            <TextInput id="clientId" value={user?.clientID || ''} isDisabled />
          </FormGroup>

          <FormGroup
            label="Sub"
            fieldId="sub"
            labelHelp={
              <Popover
                triggerRef={subHelpRef}
                headerContent={<div>This is our external key to Keycloak ID, it is immutable.</div>}
                bodyContent={
                  <div>
                    Your <em>sub</em>, or keycloak ID connects you to to our authentication platform.
                  </div>
                }
              >
                <FormGroupLabelHelp ref={subHelpRef} aria-label="More info for name field" />
              </Popover>
            }
          >
            <TextInput id="sub" value={user?.sub || ''} isDisabled />
          </FormGroup>
          <FormGroup
            label="Email"
            fieldId="email"
            labelHelp={
              <Popover
                triggerRef={emailHelpRef}
                headerContent={<div>Your professional email</div>}
                bodyContent={
                  <div>
                    This email will be used for system communication and password recovery. You may not change your
                    email address because it will also be used as your login.
                  </div>
                }
              >
                <FormGroupLabelHelp ref={emailHelpRef} aria-label="More info for name field" />
              </Popover>
            }
          >
            <TextInput id="email" name="email" type="email" value={formData.email} isDisabled={true} />
          </FormGroup>
        </Form>
      </CardBody>
      <CardTitle>User Profile</CardTitle>
      <CardBody>
        <Form onSubmit={handleSubmit}>
          {/* Editable fields */}
          <FormGroup label="First Name" fieldId="firstname" isRequired>
            <TextInput id="firstname" name="firstname" value={formData.firstname} onChange={handleInputChange} />
          </FormGroup>

          <FormGroup label="Last Name" fieldId="lastname" isRequired>
            <TextInput id="lastname" name="lastname" value={formData.lastname} onChange={handleInputChange} />
          </FormGroup>

          <ActionGroup>
            <Button variant="primary" type="submit" onClick={() => {}} isDisabled={!isChanged}>
              Save
            </Button>
            {isChanged && (
              <Button variant="secondary" type="button" onClick={() => handleCancel()}>
                Cancel
              </Button>
            )}
          </ActionGroup>
        </Form>
      </CardBody>
    </Card>
  );
}

export default UserProfileForm;
